import { MutableRefObject, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiCheck, HiDownload, HiExclamationCircle } from "react-icons/hi";
import { FaCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import { useCopyToClipboard } from "../atoms/useCopyToClipboard";

interface LinkActionsProps {
  url: string;
  qrGeneratorRef: MutableRefObject<any>;
  downloadFileName: string;
  downloadExportSize?: number;
}

const LinkActions = ({
  url,
  qrGeneratorRef,
  downloadFileName,
  downloadExportSize = 2048,
}: LinkActionsProps) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const { status: copyStatus, copy } = useCopyToClipboard();
  const isCopied = copyStatus === "success";

  const handleDownloadQrCode = async () => {
    if (!qrGeneratorRef.current) return;

    setIsDownloading(true);
    try {
      const blob = await qrGeneratorRef.current.getBlob({
        ext: "png",
        exportSize: downloadExportSize,
      });

      if (blob) {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(t("components.linkActions.downloadFailed"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <button
        onClick={() => copy(url)}
        disabled={isCopied}
        className="w-full flex items-center gap-2 px-2 py-3 bg-gray-100 rounded-xl hover:bg-gray-200 hover:border-gray-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
      >
        {copyStatus === "success" ? (
          <HiCheck className="text-green-600" size={20} />
        ) : copyStatus === "error" ? (
          <HiExclamationCircle className="text-red-600" size={20} />
        ) : (
          <FaCopy className="text-gray-400" size={20} />
        )}

        <span className="text-gray-900 font-medium text-sm flex-1 text-left truncate">
          {copyStatus === "success"
            ? t("components.linkActions.copied")
            : copyStatus === "error"
            ? t("components.linkActions.copyFailed")
            : url}
        </span>
      </button>

      <button
        onClick={handleDownloadQrCode}
        disabled={isDownloading}
        className="w-full flex items-center gap-2 px-2 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
      >
        <HiDownload className="text-gray-400" size={20} />

        <span className="text-gray-900 font-medium text-sm flex-1 text-left">
          {isDownloading
            ? t("components.linkActions.downloading")
            : t("dashboard.features.lmc.qrModal.downloadQrBtn")}
        </span>
      </button>
    </div>
  );
};

export default LinkActions;
